import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { GoogleMap, Marker, MarkerF, Polyline } from '@react-google-maps/api';
import { getStopColor } from './LoopedItinerary';
import { getDirections } from '../../../services/directionsService';

// Create vehicle icon SVG
const createVehicleIcon = (color = '#1F2937') => {
  const svgIcon = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <g>
        <!-- Vehicle shadow -->
        <ellipse cx="20" cy="35" rx="16" ry="3" fill="#000000" opacity="0.2"/>
        <!-- Vehicle body -->
        <rect x="8" y="15" width="24" height="15" rx="3" fill="${color}" stroke="#FFFFFF" stroke-width="2"/>
        <!-- Vehicle front -->
        <rect x="10" y="10" width="20" height="8" rx="4" fill="${color}" stroke="#FFFFFF" stroke-width="2"/>
        <!-- Wheels -->
        <circle cx="13" cy="30" r="4" fill="#374151" stroke="#FFFFFF" stroke-width="2"/>
        <circle cx="27" cy="30" r="4" fill="#374151" stroke="#FFFFFF" stroke-width="2"/>
        <!-- Windows -->
        <rect x="12" y="12" width="16" height="6" rx="2" fill="#E5E7EB" stroke="#D1D5DB" stroke-width="1"/>
        <!-- Windshield -->
        <rect x="14" y="14" width="12" height="3" rx="1" fill="#F3F4F6"/>
        <!-- Headlights -->
        <circle cx="11" cy="14" r="1.5" fill="#FEF3C7" stroke="#F59E0B" stroke-width="0.5"/>
        <circle cx="29" cy="14" r="1.5" fill="#FEF3C7" stroke="#F59E0B" stroke-width="0.5"/>
        <!-- Direction indicator (small arrow) -->
        <polygon points="20,5 23,10 17,10" fill="#3B82F6" stroke="#FFFFFF" stroke-width="1"/>
      </g>
    </svg>
  `)}`;
  
  return {
    url: svgIcon,
    scaledSize: new window.google.maps.Size(40, 40),
    anchor: new window.google.maps.Point(20, 20)
  };
};

const containerStyle = {
  width: '100%',
  height: '100%',
};

const mapOptions = {
  disableDefaultUI: true,
  clickableIcons: false,
  keyboardShortcuts: false,
  gestureHandling: 'greedy',   // mobile-friendly gestures


  styles: [
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  ],
};

// Generate consistent color for a vehicle based on its ID
const generateVehicleColor = (vehicleId) => {
  if (!vehicleId) return '#6B7280'; // Default gray
  
  // Create a hash from the vehicleId
  let hash = 0;
  const idString = String(vehicleId);
  for (let i = 0; i < idString.length; i++) {
    hash = idString.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Generate HSL color with good saturation and lightness for visibility
  const hue = Math.abs(hash) % 360;
  const saturation = 70 + (Math.abs(hash) % 20); // 70-90%
  const lightness = 50 + (Math.abs(hash) % 20); // 50-70%
  
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

function LoopedWaypointMap({
  isPhone,
  center,
  vehiclePosition,
  appLocation,
  onMapLoad,
  waypoint,
  expectedTime,
  onDirectionsCalculated,
  isAccurate,
  handleRequestDirections,
  currentLoopStops = [],
  currentLoopIteration = 0,
  onMapMoved,
  groupVehicles = new Map(),
  routeDetails = null, // Route details including start/end coordinates
  showPolylines = true, // Control whether to show polylines/directions
  fleetSettings,
}) {
  const [map, setMap] = useState(null);
  const [directionsPath, setDirectionsPath] = useState([]);
  const [isCalculatingDirections, setIsCalculatingDirections] = useState(false);
  const [lastDirectionsTime, setLastDirectionsTime] = useState(null);
  const [lastValidState, setLastValidState] = useState(null);
  const [mapReady, setMapReady] = useState(false);

  // Constants
  const DIRECTIONS_THROTTLE_TIME = 2 * 60 * 1000; // 2 minutes in milliseconds

  // Stable polyline options
  const polylineOptions = useMemo(() => ({
    strokeColor: '#4F46E5', // Indigo for loop
    strokeOpacity: 0.8,
    strokeWeight: 4,
    zIndex: 50
  }), []);

  // Get the map center based on real GPS positions
  const getMapCenter = useCallback(() => {
    if (vehiclePosition) {
      return vehiclePosition;
    } else if (appLocation) {
      return appLocation; // Fallback to app location if available
    }
    return null; // No valid position available
  }, [appLocation, vehiclePosition]);

  // Get the starting position for directions (prefer appLocation for real GPS, fallback to vehiclePosition)
  const getStartPosition = useCallback(() => {
    return appLocation || vehiclePosition;
  }, [appLocation, vehiclePosition]);

  const mapCenter = getMapCenter();

  // Get the next 4 stops including current waypoint
  const getUpcomingStops = useCallback(() => {
    if (!waypoint || !currentLoopStops || currentLoopStops.length === 0) {
      return [];
    }

    // Find current waypoint index
    const currentIndex = currentLoopStops.findIndex(stop => 
      stop.Id === waypoint.Id || stop.StopId === waypoint.StopId
    );
    
    if (currentIndex !== -1) {
      // Get current + next 3 stops (total of 4), cycling around the loop if needed
      const stops = [];
      for (let i = 0; i < 4 && currentLoopStops.length > 0; i++) {
        const stopIndex = (currentIndex + i) % currentLoopStops.length;
        stops.push(currentLoopStops[stopIndex]);
      }
      return stops;
    } else {
      // Fallback: use first 4 stops if current waypoint not found
      return currentLoopStops.slice(0, 4);
    }
  }, [waypoint, currentLoopStops]);

  // Check if we should calculate new directions (throttling)
  const shouldCalculateDirections = useCallback(() => {
    const now = Date.now();
    
    // Don't calculate if we don't have required components or already calculating
    const startPosition = getStartPosition();
    const upcomingStops = getUpcomingStops();
    
    if (!map || !startPosition || !waypoint || 
        upcomingStops.length === 0 || isCalculatingDirections) {
      return false;
    }

    // Always calculate if this is the first time
    if (!lastDirectionsTime) {
      return true;
    }
    
    // Calculate if enough time has passed OR if waypoint changed
    const waypointChanged = !lastValidState || lastValidState.waypointId !== waypoint?.Id;
    const timeThrottlePassed = (now - lastDirectionsTime) >= DIRECTIONS_THROTTLE_TIME;
    
    return waypointChanged || timeThrottlePassed;
  }, [map, getStartPosition, getUpcomingStops, waypoint, lastDirectionsTime, lastValidState, isCalculatingDirections, DIRECTIONS_THROTTLE_TIME]);

  // Calculate directions from start position through upcoming stops using OSRM/Google
  const calculateDirections = useCallback(async () => {
    if (!showPolylines || !shouldCalculateDirections()) {
      return;
    }

    const startPosition = getStartPosition();
    const upcomingStops = getUpcomingStops();

    if (!startPosition || !waypoint || upcomingStops.length === 0 || !map) {
      return;
    }

    setIsCalculatingDirections(true);

    // Filter valid stops with coordinates
    const validStops = upcomingStops.filter(stop => {
      const lat = stop.Lat || stop.Latitude || stop.lat;
      const lng = stop.Lon || stop.Longitude || stop.lng;
      return stop && lat && lng && !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lng));
    });

    if (validStops.length === 0) {
      console.log('No valid upcoming stops with coordinates found');
      setIsCalculatingDirections(false);
      return;
    }

    console.log(`Calculating looped directions from ${startPosition.lat},${startPosition.lng} through ${validStops.length} stops`);

    try {
      const origin = { lat: startPosition.lat, lng: startPosition.lng };
      
      const intermediateWaypoints = validStops.slice(0, -1).map(stop => ({
        lat: parseFloat(stop.Lat || stop.Latitude || stop.lat), 
        lng: parseFloat(stop.Lon || stop.Longitude || stop.lng) 
      }));

      const destination = { 
        lat: parseFloat(validStops[validStops.length - 1].Lat || validStops[validStops.length - 1].Latitude || validStops[validStops.length - 1].lat), 
        lng: parseFloat(validStops[validStops.length - 1].Lon || validStops[validStops.length - 1].Longitude || validStops[validStops.length - 1].lng) 
      };

      const osrmUrl = fleetSettings?.osrmUrl || null;
      const result = await getDirections({ origin, destination, waypoints: intermediateWaypoints, osrmUrl });

      setIsCalculatingDirections(false);

      if (result && result.path.length > 0) {
        setDirectionsPath(result.path);
        setLastDirectionsTime(Date.now());
        setLastValidState({
          waypointId: waypoint.Id,
          position: startPosition,
          stopsCount: validStops.length
        });
        console.log('Looped directions calculated via', result.source, 'successfully');
      } else {
        setDirectionsPath([]);
      }
    } catch (error) {
      console.error('Error calculating looped directions:', error);
      setIsCalculatingDirections(false);
      setDirectionsPath([]);
    }
  }, [showPolylines, shouldCalculateDirections, getStartPosition, getUpcomingStops, waypoint, map, fleetSettings?.osrmUrl]);

  // Main effect to trigger directions calculation when map and dependencies are ready
  useEffect(() => {
    if (map && mapReady) {
      const timeoutId = setTimeout(() => {
        calculateDirections();
      }, 300);
      
      return () => clearTimeout(timeoutId);
    }
  }, [map, mapReady, waypoint, appLocation, vehiclePosition, calculateDirections]);



  const onLoad = useCallback((mapInstance) => {
    console.log('LoopedWaypointMap: Map loaded');
    setMap(mapInstance);
    
    // Set map as ready after a delay to ensure it's fully loaded
    setTimeout(() => {
      console.log('LoopedWaypointMap: Setting map as ready');
      setMapReady(true);
    }, 200);
    
    onMapLoad(mapInstance);
  }, [onMapLoad]);

  // Debug logging for vehicle position
  useEffect(() => {
    console.log('LoopedWaypointMap Debug - Vehicle Position:', {
      vehiclePosition,
      appLocation,
      mapCenter,
      mapReady,
      hasVehiclePosition: !!vehiclePosition,
      vehiclePositionCoords: vehiclePosition ? `${vehiclePosition.lat}, ${vehiclePosition.lng}` : 'none'
    });
  }, [vehiclePosition, appLocation, mapCenter, mapReady]);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const handleMapClick = () => {
    if (onMapMoved && map) {
      onMapMoved(map.getCenter().toJSON());
    }
  };

  // Get marker color based on stop ID for consistency
  const getStopMarkerColor = (stop, index) => {
    const stopId = stop.StopId || stop.ItineraryId || stop.Id || stop.stopId;
    return getStopColor(stopId);
  };

  // Don't render the map until we have a valid center position
  if (!mapCenter) {
    return (
      <div className="relative w-full h-full border rounded-lg shadow-sm flex items-center justify-center">
        <div className="text-gray-500 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <div>Waiting for location...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full border rounded-lg shadow-sm">
      {/* Loop Progress Indicator */}
      {/* Google Map */}
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={mapCenter}
        zoom={isPhone ? 14 : 15}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={mapOptions}
        onClick={handleMapClick}
      >
        {/* Route polyline from directions service (OSRM or Google) */}
        {showPolylines && directionsPath.length > 1 && (
          <Polyline
            path={directionsPath}
            options={polylineOptions}
          />
        )}
        {/* Vehicle Position Marker - only render when map is ready */}
        {mapReady && vehiclePosition && vehiclePosition.lat && vehiclePosition.lng && (
          <Marker
            position={vehiclePosition}
            icon={createVehicleIcon('#2563EB')} // Blue vehicle
            title="Shuttle Vehicle"
            zIndex={1000}
            animation={window.google.maps.Animation.DROP}
          />
        )}

        {/* Current Location Marker (if different from vehicle position) - only render when map is ready */}
        {mapReady && mapCenter && mapCenter.lat && mapCenter.lng && (!vehiclePosition || 
          (mapCenter.lat !== vehiclePosition.lat || mapCenter.lng !== vehiclePosition.lng)) && (
          <Marker
            position={mapCenter}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 6,
              fillColor: isAccurate ? '#3B82F6' : '#EF4444',
              fillOpacity: 1,
              strokeColor: '#FFFFFF',
              strokeWeight: 2,
            }}
            title="Your Location"
            zIndex={999}
          />
        )}

        {/* Current Loop Stops - only render when map is ready */}
        {mapReady && currentLoopStops && currentLoopStops.length > 0 && currentLoopStops.map((stop, index) => {
          // Extract coordinates with fallbacks for different property names
          const lat = parseFloat(stop.Latitude || stop.Lat || stop.lat);
          const lng = parseFloat(stop.Longitude || stop.Lon || stop.lng);
          
          // Skip stops with invalid coordinates
          if (isNaN(lat) || isNaN(lng)) {
            console.warn(`Skipping stop with invalid coordinates:`, stop);
            return null;
          }
          
          const stopColor = getStopMarkerColor(stop, index);
          const isCurrent = stop.currentWaypoint || (waypoint && (stop.Id === waypoint.Id || stop.StopId === waypoint.StopId));
          const stopName = stop.StopName || stop.LandmarkName || stop.Name || 'Unknown Stop';
          const stopId = stop.StopId || stop.ItineraryId || stop.Id || index;
          
          
          return (
            <MarkerF
              key={`loop_${currentLoopIteration}_${stopId}_${index}`}
              position={{ lat, lng }}
              title={`${stopName} (Loop ${currentLoopIteration + 1})`}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 10, // Same size for all stops
                fillColor: stopColor,
                fillOpacity: 1,
                strokeColor: isCurrent ? '#3B82F6' : '#FFFFFF',
                strokeWeight: isCurrent ? 3 : 2,
              }}
              zIndex={isCurrent ? 900 : 800}
              visible={true}
            />
          );
        }).filter(Boolean)}

        {/* Group Vehicle markers - show other vehicles in the same route group - only render when map is ready */}
        {mapReady && groupVehicles && groupVehicles.size > 0 && Array.from(groupVehicles.values()).map((vehicle) => {
          // Validate vehicle has proper coordinates
          if (!vehicle || !vehicle.latitude || !vehicle.longitude || 
              isNaN(parseFloat(vehicle.latitude)) || isNaN(parseFloat(vehicle.longitude))) {
            return null;
          }
          
          const vehicleColor = generateVehicleColor(vehicle.vehicleId);
          const isStale = new Date() - new Date(vehicle.timestamp) > 5 * 60 * 1000; // 5 minutes stale threshold
          
          return (
            <Marker
              key={`group-vehicle-${vehicle.vehicleId}`}
              position={{ lat: parseFloat(vehicle.latitude), lng: parseFloat(vehicle.longitude) }}
              icon={createVehicleIcon(vehicleColor)}
              title={`Vehicle ${vehicle.vehicleId}${vehicle.routeId ? ` (Route ${vehicle.routeId})` : ''}${isStale ? ' - Last seen ' + new Date(vehicle.timestamp).toLocaleTimeString() : ''}`}
              zIndex={800}
              opacity={isStale ? 0 : 1.0}
            />
          );
        }).filter(Boolean)}
      </GoogleMap>
    </div>
  );
}

export default LoopedWaypointMap;
